import React, { useState } from 'react';
import { Shield, Award, Clock, Users, CheckCircle, TrendingUp, Mail, User, Phone, MessageSquare, FileCheck, Scale, Globe, Zap, Target, Lock } from 'lucide-react';
import PricingSection from '../components/PricingSection';
import TestimonialsSlider from '../components/TestimonialsSlider';
import UniversalPopupForm from '../components/UniversalPopupForm';
import SEO from '../components/SEO';
import { toast } from 'sonner';
import { submitLead, fetchGeolocation } from '../services/apiService';
import { usePricing } from '../context/PricingContext';

const Home = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { selectedPackage, tmSignUpType, resetPackage } = usePricing();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+1',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await submitLead({
        ...formData,
        packageTitle: selectedPackage.packageTitle,
        packageAmount: selectedPackage.packageAmount,
        tmSignUpType
      });
      
      if (result.success) {
        toast.success(result.message);
        // Redirect to application page
        window.location.href = `/application/main?customerid=${result.customerId}`;
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChatClick = () => {
    // Zendesk chat widget will handle this
    if (window.LiveChatWidget) {
      window.LiveChatWidget.call('maximize');
    } else {
      toast.info('Chat is loading... Please try again in a moment.');
    }
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Trademarkflare | US Trademark Search and Filing | File Your Mark"
        description="Trademarkflare offers step by step USPTO guidance. Talk to a team that responds fast."
        keywords="trademark registration Canada, CIPO trademark services, Canadian IP protection, brand registration Halifax, trademark lawyers Canada"
      />
      
      {/* Hero Section with Form on Right */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1521791136064-7986c2920216"
            alt="Professional business handshake"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-900/90 to-brand-700/85"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div className="text-white">
              <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                <span className="text-white font-semibold text-sm">🍁 Trademark Flare - Your Canadian IP Partner</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Ignite Your Brand's Legal Protection
              </h1>
              <p className="text-xl text-brand-100 mb-8 leading-relaxed">
                Expert trademark registration services from Halifax to Victoria. Protect your business identity with CIPO-certified professionals who deliver results.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <CheckCircle className="w-6 h-6 text-brand-300 flex-shrink-0" />
                  <span className="text-lg font-semibold">99% Approval Rate</span>
                </div>
                <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <CheckCircle className="w-6 h-6 text-brand-300 flex-shrink-0" />
                  <span className="text-lg font-semibold">1000+ Brands Protected</span>
                </div>
                <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <CheckCircle className="w-6 h-6 text-brand-300 flex-shrink-0" />
                  <span className="text-lg font-semibold">24/7 Customer Service</span>
                </div>
                <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg p-4">
                  <CheckCircle className="w-6 h-6 text-brand-300 flex-shrink-0" />
                  <span className="text-lg font-semibold">Expert CIPO Guidance</span>
                </div>
              </div>
            </div>

            {/* Right Side - Form (Reduced Height) */}
            <div>
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6">
                <div className="text-center mb-4">
                  <div className="inline-block bg-brand-600 rounded-full p-3 mb-3">
                    <Shield className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-1">Begin Your Brand Journey</h2>
                  <p className="text-sm text-gray-600">Professional trademark protection starts here</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <User className="w-3 h-3 inline mr-1" />
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Mail className="w-3 h-3 inline mr-1" />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Phone className="w-3 h-3 inline mr-1" />
                      Contact Number *
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={formData.countryCode}
                        onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                        className="w-24 flex-shrink-0 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm"
                      >
                        <option value="+1">🇨🇦 +1 (Canada)</option>
                        <option value="+1">🇺🇸 +1 (USA)</option>
                        <option value="+44">🇬🇧 +44 (UK)</option>
                        <option value="+91">🇮🇳 +91 (India)</option>
                        <option value="+86">🇨🇳 +86 (China)</option>
                        <option value="+81">🇯🇵 +81 (Japan)</option>
                        <option value="+82">🇰🇷 +82 (South Korea)</option>
                        <option value="+61">🇦🇺 +61 (Australia)</option>
                        <option value="+49">🇩🇪 +49 (Germany)</option>
                        <option value="+33">🇫🇷 +33 (France)</option>
                        <option value="+39">🇮🇹 +39 (Italy)</option>
                        <option value="+34">🇪🇸 +34 (Spain)</option>
                        <option value="+7">🇷🇺 +7 (Russia)</option>
                        <option value="+55">🇧🇷 +55 (Brazil)</option>
                        <option value="+52">🇲🇽 +52 (Mexico)</option>
                        <option value="+27">🇿🇦 +27 (South Africa)</option>
                        <option value="+971">🇦🇪 +971 (UAE)</option>
                        <option value="+65">🇸🇬 +65 (Singapore)</option>
                        <option value="+31">🇳🇱 +31 (Netherlands)</option>
                        <option value="+46">🇸🇪 +46 (Sweden)</option>
                      </select>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          if (value.length <= 10) {
                            setFormData({ ...formData, phone: value });
                          }
                        }}
                        className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm"
                        placeholder="4165550123"
                        maxLength="10"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <MessageSquare className="w-3 h-3 inline mr-1" />
                      Message
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all resize-none text-sm"
                      placeholder="Tell us about your trademark..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-brand-600 to-brand-700 text-white font-semibold py-3 px-6 rounded-lg hover:from-brand-700 hover:to-brand-800 transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 shadow-lg text-sm"
                  >
                    {loading ? 'Submitting...' : 'Protect My Brand Now'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-brand-600 mb-2">1000+</div>
              <div className="text-gray-600">Trademarks Protected</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-brand-600 mb-2">99%</div>
              <div className="text-gray-600">Success Rate</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-brand-600 mb-2">24/7</div>
              <div className="text-gray-600">Customer Service</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-brand-600 mb-2">15+ Years</div>
              <div className="text-gray-600">Industry Experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section 1 - Image Left, Content Right */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-600 to-brand-800 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85"
                alt="Professional trademark documentation"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="bg-brand-100 rounded-full p-2">
                    <CheckCircle className="w-5 h-5 text-brand-600" />
                  </div>
                  <span className="font-bold text-gray-900">CIPO Certified</span>
                </div>
                <p className="text-sm text-gray-600">Expert guidance for seamless approval</p>
              </div>
            </div>

            <div>
              <div className="inline-block bg-brand-100 text-brand-700 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Expert CIPO Navigation
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Master the Canadian IP Process
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                The Canadian Intellectual Property Office demands precision. Our seasoned experts have successfully registered over 1000 trademarks, ensuring your application sails through with confidence.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="bg-brand-100 rounded-lg p-3 flex-shrink-0">
                    <FileCheck className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Meticulous Documentation</h3>
                    <p className="text-gray-600">Every CIPO requirement met with exacting standards to maximize approval chances.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-brand-100 rounded-lg p-3 flex-shrink-0">
                    <Zap className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Accelerated Processing</h3>
                    <p className="text-gray-600">Our dedicated support team is available 24/7 to assist you with your trademark needs.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-brand-100 rounded-lg p-3 flex-shrink-0">
                    <Target className="w-6 h-6 text-brand-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Strategic Classification</h3>
                    <p className="text-gray-600">Intelligent class selection delivers comprehensive, future-proof brand protection.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section 2 - Content Left, Image Right */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block bg-brand-100 text-brand-700 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Nationwide Protection
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Shield Your Brand Coast to Coast
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                A registered trademark grants you exclusive nationwide rights across every Canadian province and territory. Don't let competitors dilute your hard-earned brand equity.
              </p>
              <div className="bg-gradient-to-br from-brand-50 to-brand-100 rounded-2xl p-8 mb-6">
                <h3 className="font-bold text-gray-900 mb-4 text-xl">Your Complete Protection Package:</h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <div className="bg-brand-600 rounded-full p-1">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Exclusive nationwide usage rights</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-brand-600 rounded-full p-1">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Legal authority to prevent infringement</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-brand-600 rounded-full p-1">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Renewable protection (10-year terms)</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="bg-brand-600 rounded-full p-1">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-gray-700">Valuable asset for licensing & transfers</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => { resetPackage(); setIsPopupOpen(true); }}
                className="bg-gradient-to-r from-brand-600 to-brand-700 text-white font-semibold px-8 py-4 rounded-lg hover:from-brand-700 hover:to-brand-800 transform hover:scale-105 transition-all duration-200 shadow-lg"
              >
                Start My Protection
              </button>
            </div>

            <div className="order-1 lg:order-2 relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-400 to-brand-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf"
                alt="Business team celebrating success"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl p-4">
                <div className="flex items-center space-x-3">
                  <div className="bg-brand-600 rounded-full p-2">
                    <Lock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Protected</div>
                    <div className="text-sm text-gray-600">All of Canada</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section 3 - Image Left, Content Right */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-purple-600 to-brand-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.pexels.com/photos/8441784/pexels-photo-8441784.jpeg"
                alt="Legal consultation session"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -top-6 -left-6 bg-gradient-to-r from-brand-600 to-brand-800 text-white rounded-xl shadow-xl p-6 max-w-xs">
                <Scale className="w-8 h-8 mb-2" />
                <div className="font-bold text-lg">Legal Excellence</div>
                <div className="text-sm text-brand-100">Expert trademark attorneys</div>
              </div>
            </div>

            <div>
              <div className="inline-block bg-purple-100 text-purple-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Professional Expertise
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Why Professional Help Matters
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                DIY trademark applications have a 40% higher rejection rate. Our experts know the common pitfalls and ensure your application is done right the first time.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-red-100">
                  <div className="text-3xl font-bold text-red-600 mb-2">40%</div>
                  <div className="text-sm text-gray-600">DIY Rejection Rate</div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-green-100">
                  <div className="text-3xl font-bold text-green-600 mb-2">98%</div>
                  <div className="text-sm text-gray-600">Our Success Rate</div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-brand-600 flex-shrink-0" />
                  <span className="text-gray-700">Avoid costly mistakes and application fees</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-brand-600 flex-shrink-0" />
                  <span className="text-gray-700">Save months of back-and-forth with CIPO</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-brand-600 flex-shrink-0" />
                  <span className="text-gray-700">Get it right the first time with expert guidance</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section 4 - Content Left, Image Right */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block bg-brand-100 text-brand-700 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Trusted Across Canada
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Join 1000+ Protected Canadian Businesses
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                From Halifax startups to Calgary enterprises, Canadian businesses trust Trademark Flare to safeguard their most valuable asset—their brand identity.
              </p>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-brand-600 to-brand-700 rounded-full p-3 flex-shrink-0">
                    <Globe className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg">Complete Provincial Coverage</h3>
                    <p className="text-gray-600">From BC to the Maritimes, we protect brands nationwide.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-brand-600 to-brand-700 rounded-full p-3 flex-shrink-0">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg">Industry Leader</h3>
                    <p className="text-gray-600">Serving tech, retail, hospitality, and professional services sectors.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-gradient-to-r from-brand-600 to-brand-700 rounded-full p-3 flex-shrink-0">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg">Personalized Support</h3>
                    <p className="text-gray-600">Dedicated case managers shepherd you through the entire process.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2 relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-400 to-brand-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1650784854790-fb6c2ed400d3"
                alt="Professional business team"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <div className="text-4xl font-bold text-brand-600 mb-1">1000+</div>
                <div className="text-gray-700 font-semibold">Brands Protected</div>
                <div className="text-sm text-gray-500 mt-1">From coast to coast</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Canadian Businesses Trust Us
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We specialize in Canadian trademark law and CIPO procedures to protect your brand effectively
            </p>
          </div>

          <div className="md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto snap-x snap-mandatory gap-4 pb-4 md:pb-0 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100 min-w-full md:min-w-0 snap-center">
              <div className="bg-brand-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-7 h-7 text-brand-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">CIPO Experts</h3>
              <p className="text-gray-600 leading-relaxed">
                Deep expertise in Canadian Intellectual Property Office procedures and requirements for seamless registration.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100 min-w-full md:min-w-0 snap-center">
              <div className="bg-brand-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                <Award className="w-7 h-7 text-brand-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">High Success Rate</h3>
              <p className="text-gray-600 leading-relaxed">
                98% approval rate thanks to thorough searches and expert preparation of applications.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100 min-w-full md:min-w-0 snap-center">
              <div className="bg-brand-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                <Clock className="w-7 h-7 text-brand-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Fast Processing</h3>
              <p className="text-gray-600 leading-relaxed">
                Quick turnaround times with 24-48 hour initial response and efficient CIPO filing.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100 min-w-full md:min-w-0 snap-center">
              <div className="bg-brand-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-7 h-7 text-brand-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Dedicated Support</h3>
              <p className="text-gray-600 leading-relaxed">
                Personal case managers guide you through every step of the Canadian trademark process.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100 min-w-full md:min-w-0 snap-center">
              <div className="bg-brand-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                <CheckCircle className="w-7 h-7 text-brand-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Comprehensive Search</h3>
              <p className="text-gray-600 leading-relaxed">
                Thorough database searches to prevent conflicts and ensure your trademark is unique.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300 border border-gray-100 min-w-full md:min-w-0 snap-center">
              <div className="bg-brand-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="w-7 h-7 text-brand-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Ongoing Monitoring</h3>
              <p className="text-gray-600 leading-relaxed">
                Continuous trademark monitoring to protect your brand from potential infringements.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <div id="pricing">
        <PricingSection />
      </div>

      {/* Testimonials */}
      <TestimonialsSlider />

      {/* Final CTA Section with Canadian Location Background */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1714428586252-d72da2ece0a0"
            alt="Toronto skyline and waterfront"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-900/95 via-brand-800/90 to-brand-700/95"></div>
          {/* Animated overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-brand-900/50 to-transparent"></div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Floating badge */}
          <div className="inline-block animate-bounce mb-8">
            <div className="bg-white/20 backdrop-blur-md rounded-full px-6 py-3 border-2 border-white/30">
              <span className="text-white font-bold text-sm">🍁 Canada's #1 Trademark Service</span>
            </div>
          </div>

          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Your Brand Deserves<br />
            <span className="bg-gradient-to-r from-brand-300 to-brand-400 bg-clip-text text-transparent">
              Official Canadian Protection
            </span>
          </h2>
          
          <p className="text-2xl text-brand-100 mb-12 max-w-3xl mx-auto leading-relaxed">
            Join hundreds of Canadian businesses protecting their brands with CIPO registration. 
            Start your trademark journey today with Canada's trusted experts.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
            <button
              onClick={() => { resetPackage(); setIsPopupOpen(true); }}
              className="group relative bg-white text-brand-600 font-bold px-10 py-5 rounded-xl hover:bg-gray-100 transform hover:scale-110 transition-all duration-300 shadow-2xl"
            >
              <span className="relative z-10 flex items-center space-x-2">
                <Shield className="w-6 h-6" />
                <span className="text-lg">Start Your Registration</span>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-brand-400 to-brand-500 rounded-xl opacity-0 group-hover:opacity-20 blur transition-opacity duration-300"></div>
            </button>
            
            <button
              onClick={handleChatClick}
              className="group border-3 border-white text-white font-bold px-10 py-5 rounded-xl hover:bg-white hover:text-brand-600 transition-all duration-300 shadow-2xl backdrop-blur-sm"
            >
              <span className="flex items-center space-x-2">
                <span className="text-lg">Chat Now</span>
                <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
              </span>
            </button>
          </div>

          {/* Trust indicators */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">500+</div>
              <div className="text-brand-100 text-sm">Brands Protected</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">98%</div>
              <div className="text-brand-100 text-sm">Success Rate</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">24h</div>
              <div className="text-brand-100 text-sm">Fast Response</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">10+</div>
              <div className="text-brand-100 text-sm">Provinces Served</div>
            </div>
          </div>

          <p className="text-brand-200 mt-12 text-sm">
            Protecting Canadian businesses from coast to coast • CIPO Registered Agents
          </p>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-brand-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-brand-400/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </section>

      <UniversalPopupForm isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default Home;
